import { t } from '@lingui/macro';
import { withStyles } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { updateSettings } from 'api/api';
import { AssetImage } from 'assets/Asset';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import chartsStyle from 'MUI_Kit/assets/jss/material-dashboard-pro-react/views/chartsStyle';
import Card from 'MUI_Kit/components/Card/Card';
import CardBody from 'MUI_Kit/components/Card/CardBody';
import CardHeader from 'MUI_Kit/components/Card/CardHeader';
import CardIcon from 'MUI_Kit/components/Card/CardIcon';
import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { compose } from 'redux';
import { Types } from 'redux/type';
import { xPath } from 'testingClasses';
import DV from 'variables/DV';
import { handleApi } from 'variables/utils';
import { getDateTime } from '../utils';

const CHART_BODY_STYLE = {
  width: '100%',
  aspectRatio: '2 / 1',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
};

const InsightStatisticCard = ({
  classes,
  title,
  loading,
  error,
  showEmpty,
  emptyContent,
  canDelete,
  code,
  onLoadMore,
  loadingMore,
  children,
}) => {
  const dispatch = useDispatch();
  const graphs = useSelector((state) => state.common.graphs);

  const deleteChart = async () => {
    const oldGraphs = [...graphs];
    const indexInGraphs = graphs.findIndex((graph) => graph === code);
    if (indexInGraphs < 0) return;

    const newGraphs = [...graphs];
    newGraphs.splice(indexInGraphs, 1);
    dispatch({ type: Types.SET_GRAPHS, payload: newGraphs });

    const { error: deleteError } = await handleApi(updateSettings({ statistic_graphs: newGraphs }));
    if (deleteError) {
      dispatch({ type: Types.SET_GRAPHS, payload: oldGraphs });
      DV.showNotify(deleteError, 'danger');
    }
  };
  const renderEmpty = () => {
    if (emptyContent) {
      return (
        <div className="flex-center-col" style={{ width: '100%', height: '100%' }}>
          {emptyContent}
        </div>
      );
    }
    return (
      <div className={classNames('content', ' flex_center', xPath.stateView.empty)}>
        <img src={AssetImage.Empty} alt="empty" style={{ width: '50%', maxWidth: 300 }} />
        <div style={{ height: 30 }} />
        <div>{t`Chưa có dữ liệu`}</div>
        <div style={{ height: 20 }} />
      </div>
    );
  };

  const renderLoading = () => {
    return (
      <div className="content flex_center TEST__loading">
        <img src={AssetImage.Loading} style={{ width: '80%' }} alt="loading" />
        <div style={{ height: 30 }} />
        <div>{t` Đang tải dữ liệu...`}</div>
        <div style={{ height: 20 }} />
      </div>
    );
  };

  const renderError = () => {
    const { detail, error: errorMsg } = error || {};
    return (
      <div className="content flex_center TEST__loading">
        <img src={AssetImage.Error} style={{ width: '80%' }} alt="loading" />
        <div style={{ height: 30 }} />
        <div className="text-center">
          {!detail?.since && !detail?.until ? errorMsg : ''}
          {detail?.since && (
            <div>Biểu đồ này không thể hiển thị dữ liệu trước ngày {getDateTime(detail.since)}</div>
          )}
          {detail?.until && (
            <div>Biểu đồ này không thể hiển thị dữ liệu sau ngày {getDateTime(detail.until)}</div>
          )}
        </div>
        <div style={{ height: 20 }} />
      </div>
    );
  };

  const renderLoadingMore = () => {
    return (
      <div className="flex_center TEST__loading_more" style={{ padding: '8px 0' }}>
        <img src={AssetImage.Loading} style={{ width: 60 }} alt="loading" />
      </div>
    );
  };

  const handleScroll = (event) => {
    if (!onLoadMore || loadingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= 48) onLoadMore();
  };

  const renderContent = () => {
    if (loading) return renderLoading();
    if (!isEmpty(error)) return renderError();
    if (showEmpty) return renderEmpty();
    return (
      <>
        {children}
        {loadingMore && renderLoadingMore()}
      </>
    );
  };

  return (
    <div style={{ height: '100%', paddingBottom: 50 }}>
      <Card>
        <CardHeader color="warning" icon className="flex justify-between">
          <div style={{ display: 'flex' }}>
            <CardIcon color="warning">
              <i className="fal fa-sparkles" style={{ fontSize: 28 }} />
            </CardIcon>
            <div className="flex items-end">
              <h4 className={classes.cardIconTitle}>{title}</h4>
            </div>
          </div>
          {canDelete && (
            <IconButton aria-label="delete" size="small" onClick={deleteChart}>
              <CloseIcon />
            </IconButton>
          )}
        </CardHeader>
        <CardBody className="flex_center">
          <div
            style={CHART_BODY_STYLE}
            onScroll={onLoadMore ? handleScroll : undefined}>
            {renderContent()}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

InsightStatisticCard.propTypes = {
  classes: PropTypes.object,
  title: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  showEmpty: PropTypes.bool,
  emptyContent: PropTypes.node,
  canDelete: PropTypes.bool,
  code: PropTypes.string,
  onLoadMore: PropTypes.func,
  loadingMore: PropTypes.bool,
  children: PropTypes.node,
};

export default compose(withStyles(chartsStyle), memo)(InsightStatisticCard);
